import React, { useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Send, CheckCircle2 } from 'lucide-react';
import { useLanguage } from '../lib/LanguageContext';
export function CallToAction() {
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState({
    occasion: '',
    date: '',
    name: '',
    phone: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  // ⚠️ SETUP REQUIRED: Get a FREE Web3Forms access key (takes 30 seconds, no signup needed)
  // 1. Go to https://web3forms.com
  // 2. Enter cheersbylisa.events@gmail.com in the "Your Email" field
  // 3. Click "Create Access Key" — the key is sent to that email
  // 4. Paste the key below, replacing YOUR_ACCESS_KEY_HERE
  // Once done, every form submission will arrive directly in the inbox — no verification needed.
  const WEB3FORMS_ACCESS_KEY = 'YOUR_ACCESS_KEY_HERE';
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: `New Booking Request from ${formData.name}`,
          from_name: 'Cheers by Lisa Website',
          'Party Reason': formData.occasion,
          'Event Date': formData.date,
          Name: formData.name,
          Phone: formData.phone
        })
      });
      const result = await response.json();
      if (result.success) {
        setSubmitted(true);
      } else {
        throw new Error(result.message || 'Submission failed');
      }
    } catch (err) {
      setError("We couldn't send your request automatically. Please call us at (954) 655-9109 or email cheersbylisa.events@gmail.com directly.");
    } finally {
      setIsSubmitting(false);
    }
  };
  const inputClass = 'w-full bg-white/5 border border-white/20 text-white placeholder:text-white/40 px-4 py-3.5 rounded-sm focus:outline-none focus:border-[#D4A843] focus:bg-white/10 transition-all duration-200 font-light';
  const labelClass = 'block text-teal-50/90 text-sm font-medium mb-2 tracking-wide text-left';
  return <section id="booking" className="py-24 md:py-32 relative overflow-hidden flex items-center justify-center bg-[#134E4A] scroll-mt-16" data-id="element-47">
      {/* Subtle background glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#D4A843]/10 rounded-full blur-[100px] pointer-events-none" data-id="element-48"></div>

      <div className="relative z-10 text-center px-6 max-w-2xl mx-auto w-full" data-id="element-49">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight" data-id="element-50">
          {t('cta.title.part1')}{' '}
          <span className="text-[#D4A843] italic" data-id="element-51">{t('cta.title.part2')}</span>{' '}
          <br data-id="element-52" /> {t('cta.title.part3')}
        </motion.h2>

        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="text-teal-50/80 text-lg mb-12 font-light" data-id="element-53">
          {t('cta.desc')}
        </motion.p>

        <AnimatePresence mode="wait" data-id="element-54">
          {!submitted ? <motion.form key="form" onSubmit={handleSubmit} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.4
        }} className="space-y-5 text-left" data-id="element-55">
              <div data-id="element-56">
                <label htmlFor="occasion" className={labelClass} data-id="element-57">
                  {t('form.occasion.label')}
                </label>
                <input type="text" id="occasion" name="occasion" required value={formData.occasion} onChange={handleChange} placeholder={t('form.occasion.placeholder')} className={inputClass} data-id="element-58" />
              </div>

              <div data-id="element-59">
                <label htmlFor="date" className={labelClass} data-id="element-60">
                  {t('form.date.label')}
                </label>
                <input type="date" id="date" name="date" required value={formData.date} onChange={handleChange} className={`${inputClass} [color-scheme:dark]`} data-id="element-61" />
              </div>

              <div data-id="element-62">
                <label htmlFor="name" className={labelClass} data-id="element-63">
                  {t('form.name.label')}
                </label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} placeholder={t('form.name.placeholder')} className={inputClass} data-id="element-64" />
              </div>

              <div data-id="element-65">
                <label htmlFor="phone" className={labelClass} data-id="element-66">
                  {t('form.phone.label')}
                </label>
                <input type="tel" id="phone" name="phone" required value={formData.phone} onChange={handleChange} placeholder={t('form.phone.placeholder')} className={inputClass} data-id="element-67" />
              </div>

              {error && <p className="text-red-300 text-sm text-center" data-id="element-68">{error}</p>}

              <button type="submit" disabled={isSubmitting} className="w-full mt-4 bg-[#D4A843] text-[#134E4A] px-8 py-4 text-base font-semibold tracking-widest uppercase hover:bg-white transition-colors duration-300 shadow-[0_0_30px_rgba(212,168,67,0.3)] flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-69">
                {isSubmitting ? 'Sending...' : t('form.submit')}
                {!isSubmitting && <Send className="w-4 h-4" data-id="element-70" />}
              </button>
            </motion.form> : <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9
        }} transition={{
          duration: 0.5
        }} className="bg-white/5 border border-[#D4A843]/30 p-10 rounded-sm" data-id="element-71">
              <CheckCircle2 className="w-16 h-16 text-[#D4A843] mx-auto mb-4" data-id="element-72" />
              <h3 className="text-3xl font-serif text-white mb-3" data-id="element-73">
                {t('form.success.title')}
              </h3>
              <p className="text-teal-50/80 font-light leading-relaxed" data-id="element-74">
                {t('form.success.message')}
              </p>
            </motion.div>}
        </AnimatePresence>
      </div>
    </section>;
}